import { FC, ReactNode } from "react";
import { Modal, UiButton } from "@domun/component-library";
import { useTranslation } from "react-i18next";
import { ConfirmModalStyled } from "./confirm-modal.styled";
import Button from "@dashboard/modules/products/components/button/button";

type ConfirmModalStyledProps = {
  title?: string;
  copy?: string;
  icon?: ReactNode;
  continueLabel?: string;
  cancelLabel?: string;
  persuade?: boolean;
  continueLoading?: boolean;
  closeOnContinue?: boolean;
  onClose?: () => void;
  onContinue?: () => void | Promise<void>;
};

const ConfirmModal: FC<ConfirmModalStyledProps> = ({
  title,
  copy,
  icon,
  continueLabel,
  cancelLabel,
  persuade,
  continueLoading,
  closeOnContinue = true,
  onClose,
  onContinue,
}) => {
  const { t } = useTranslation();

  const handleClose = () => {
    onClose && onClose();
  };

  const handleContinue = async () => {
    if (!onContinue || continueLoading) return;
    const result = onContinue();
    if (result instanceof Promise) {
      await result;
    }
    if (closeOnContinue) {
      handleClose();
    }
  };

  return (
    <Modal
      title=""
      show={true}
      onClose={handleClose}
      content={{
        className: "fadeInDown",
        styles: {
          width: "480px",
        },
      }}
      header={{
        styles: {
          borderBottom: 0,
          boxShadow: "none",
          paddingBottom: 0,
          paddingLeft: "2rem",
        },
      }}
      body={{
        styles: {
          padding: "2rem",
          paddingTop: 0,
        },
      }}
    >
      <ConfirmModalStyled>
        <span className="confirm-modal-icon">
          {icon || <span className="icon-circle-exclamation" />}
        </span>
        <h4 className="confirm-modal-title">{title || t("confirm.title")}</h4>
        <p className="confirm-modal-copy">{copy || t("confirm.copy")}</p>
        {persuade ? (
          <>
            <Button
              appearance="dark"
              disabled={continueLoading}
              onClick={handleClose}
            >
              {cancelLabel || t("cancel")}
            </Button>
            {continueLoading ? (
              <UiButton
                isLoading={continueLoading}
                disabled={continueLoading}
                onClick={() => void handleContinue()}
              >
                {continueLabel || t("continue")}
              </UiButton>
            ) : (
              <Button appearance="danger" link onClick={() => void handleContinue()}>
                {continueLabel || t("continue")}
              </Button>
            )}
          </>
        ) : (
          <>
            {continueLoading ? (
              <UiButton
                isLoading={continueLoading}
                disabled={continueLoading}
                onClick={() => void handleContinue()}
              >
                {continueLabel || t("continue")}
              </UiButton>
            ) : (
              <Button appearance="dark" onClick={() => void handleContinue()}>
                {continueLabel || t("continue")}
              </Button>
            )}
            <Button
              appearance="dark"
              outline
              disabled={continueLoading}
              onClick={handleClose}
            >
              {cancelLabel || t("cancel")}
            </Button>
          </>
        )}
      </ConfirmModalStyled>
    </Modal>
  );
};

export default ConfirmModal;
